import { useState } from 'react';
import { Bot, Megaphone, Scale, ImagePlus, Loader2, Check, Copy, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api } from '../../lib/api';
import { waitJob, byokPayload, coverPayload } from '../../lib/architect';
import { reportKeyStatus } from '../../lib/keyStatus';

const ACTIONS = [
  ['marketing', Megaphone, 'Genera Descrizione & Tag per lo Store'],
  ['balance', Scale, 'Bilanciamento & Suggerimenti Gameplay'],
  ['cover_prompt', ImagePlus, 'Genera Prompt per Immagine di Copertina'],
];

function Result({ task, r, game, onSaved }) {
  const [busy, setBusy] = useState(false);
  const patch = async (body, msg) => {
    setBusy(true);
    try { onSaved(await api(`/admin/games/${game.id}`, { method: 'PATCH', body, auth: true })); toast.success(msg); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success('Copiato'); };
  const cover = async () => {
    setBusy(true);
    try { const { job_id } = await api('/admin/ai/cover', { method: 'POST', body: { game_id: game.id, hint: r.coverPrompt.slice(0, 400), ...(await coverPayload()) }, auth: true }); const out = JSON.parse(await waitJob(job_id)); if (out.key_status) reportKeyStatus(out.key_status, { silent: !out.key_status.low?.length }); onSaved({ ...game, has_cover: true, version: (game.version || 1) + 1 }); toast.success('Copertina generata'); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const btn = 'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold disabled:opacity-50';
  if (task === 'marketing') return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-slate-100">{r.title}</div>
      <div className="text-xs text-cyan-200">{r.shortDescription}</div>
      <div className="text-xs text-slate-300">{r.description}</div>
      <div className="flex flex-wrap gap-1">{(r.tags || []).slice(0, 4).map((t) => <span key={t} className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-300">{t}</span>)}</div>
      <button data-testid="copilot-apply-marketing" disabled={busy} onClick={() => patch({ subtitle: r.shortDescription?.slice(0, 120), description: r.description?.slice(0, 500), tags: (r.tags || []).slice(0, 4) }, 'Testi applicati al gioco')} className={`${btn} bg-cyan-600`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}Applica direttamente</button>
    </div>
  );
  if (task === 'balance') {
    const md = (r.balanceTips || []).map((t) => `- ${t}`).join('\n');
    return (
      <div className="space-y-2">
        <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-300">{(r.balanceTips || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
        <div className="flex gap-2">
          <button data-testid="copilot-apply-balance" disabled={busy} onClick={() => patch({ long_description: `${game.long_description ? `${game.long_description}\n\n` : ''}## Note di bilanciamento\n${md}`.slice(0, 8000) }, 'Note aggiunte alla descrizione completa')} className={`${btn} bg-cyan-600`}><Check className="h-3 w-3" />Aggiungi alla descrizione</button>
          <button onClick={() => copy(md)} className={`${btn} border border-white/15`}><Copy className="h-3 w-3" />Copia</button>
        </div>
        <p className="text-[10px] text-slate-500">Per applicarli al codice: incollali nella chat di THE ARCHITECT nello Studio.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-black/40 p-3 font-mono text-[11px] text-slate-200 whitespace-pre-wrap">{r.coverPrompt}</div>
      <div className="flex gap-2">
        <button data-testid="copilot-copy-prompt" onClick={() => copy(r.coverPrompt)} className={`${btn} border border-white/15`}><Copy className="h-3 w-3" />Copia prompt</button>
        <button data-testid="copilot-apply-cover" disabled={busy} title="Genera con la tua chiave OpenAI (gpt-image-1)" onClick={cover} className={`${btn} bg-violet-600`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}Genera copertina ora</button>
      </div>
    </div>
  );
}

export default function Copilot({ open, setOpen, games, gameId, setGameId, onSaved }) {
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState({});
  const game = games.find((g) => g.id === gameId) || games[0];
  const run = async (task) => {
    setBusy(task);
    try {
      const { job_id } = await api('/admin/ai/metadata', { method: 'POST', body: { game_id: game.id, task, ...(await byokPayload()) }, auth: true });
      const r = JSON.parse(await waitJob(job_id));
      setRes((x) => ({ ...x, [`${game.id}:${task}`]: r }));
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="copilot-drawer" className="bg-titan-surface border-violet-500/30 w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Bot className="h-5 w-5 text-cyan-300" />Game Studio Copilot</SheetTitle>
          <SheetDescription className="t-chat">Analizza il codice del gioco e propone testi, bilanciamento e copertina.</SheetDescription>
        </SheetHeader>
        {game && (
          <div className="mt-5 space-y-4">
            <Select value={game.id} onValueChange={setGameId}>
              <SelectTrigger data-testid="copilot-game-select" className="bg-black/30"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-titan-surface border-violet-500/30">{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}</SelectContent>
            </Select>
            {ACTIONS.map(([task, Icon, label]) => {
              const r = res[`${game.id}:${task}`];
              return (
                <div key={task} className="glass rounded-2xl p-4 space-y-3">
                  <button data-testid={`copilot-run-${task}`} onClick={() => run(task)} disabled={!!busy} className="w-full flex items-center gap-2 text-left text-xs font-bold text-slate-100 disabled:opacity-60">
                    {busy === task ? <Loader2 className="h-4 w-4 animate-spin text-cyan-300" /> : <Icon className="h-4 w-4 text-cyan-300" />}{label}
                  </button>
                  {busy === task && <div className="text-[11px] text-slate-400">L'IA sta analizzando il gioco… (20-40s)</div>}
                  {r && <div data-testid={`copilot-result-${task}`}><Result task={task} r={r} game={game} onSaved={onSaved} /></div>}
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
